import { useState, useRef, useEffect } from "react";
import { GOLD } from "../constants/temas";

export default function Player({ src, titulo, subtitulo, cor, tema }) {
    const T = tema;
    const audioRef = useRef(null);
    const progressRef = useRef(null);
    const [tocando, setTocando] = useState(false);
    const [progresso, setProgresso] = useState(0);
    const [duracao, setDuracao] = useState(0);
    const [volume, setVolume] = useState(1);
    const [arrastando, setArrastando] = useState(false);

    useEffect(() => {
        setTocando(false);
        setProgresso(0);
    }, [src]);

    const togglePlay = () => {
        const a = audioRef.current;
        if (!a) return;
        if (tocando) { a.pause(); setTocando(false); }
        else { a.play().catch(() => {}); setTocando(true); }
    };

    const pular = (seg) => {
        const a = audioRef.current;
        if (!a) return;
        a.currentTime = Math.max(0, Math.min(duracao, a.currentTime + seg));
    };

    const fmt = s => {
        if (!s || isNaN(s)) return "0:00";
        const m = Math.floor(s / 60), seg = Math.floor(s % 60);
        return `${m}:${seg.toString().padStart(2, "0")}`;
    };

    const pct = duracao ? (progresso / duracao) * 100 : 0;

    const handleProgressClick = (e) => {
        const rect = progressRef.current.getBoundingClientRect();
        const p = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        if (audioRef.current) audioRef.current.currentTime = p * duracao;
    };

    return (
        <div style={{
            background: `linear-gradient(135deg, ${T.bgCard}, ${T.bgCardSecundario})`,
            borderRadius: 20,
            padding: "18px 20px",
            border: `1px solid ${T.borda}`,
            boxShadow: tocando ? `0 8px 32px ${cor}22` : "none",
            transition: "box-shadow 0.3s",
        }}>
            <audio
                ref={audioRef}
                src={src}
                onTimeUpdate={e => !arrastando && setProgresso(e.target.currentTime)}
                onLoadedMetadata={e => setDuracao(e.target.duration)}
                onEnded={() => setTocando(false)}
            />

            {/* Info da faixa */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                <div style={{
                    width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                    background: `linear-gradient(135deg, ${cor}, ${cor}88)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 24,
                    boxShadow: tocando ? `0 4px 16px ${cor}55` : "none",
                    transition: "box-shadow 0.3s",
                }}>
                    🎵
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: T.texto, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {titulo || "Sem título"}
                    </div>
                    <div style={{ fontSize: 12, color: T.textoSecundario, marginTop: 2 }}>{subtitulo}</div>
                </div>
            </div>

            {/* Barra de progresso */}
            <div style={{ marginBottom: 6 }}>
                <div
                    ref={progressRef}
                    onClick={handleProgressClick}
                    style={{
                        height: 4, background: T.borda, borderRadius: 2,
                        cursor: src ? "pointer" : "default", position: "relative",
                        marginBottom: 6,
                    }}
                >
                    {/* Preenchimento */}
                    <div style={{
                        width: pct + "%", height: "100%",
                        background: `linear-gradient(90deg, ${cor}, ${cor}cc)`,
                        borderRadius: 2, transition: arrastando ? "none" : "width 0.1s",
                    }} />
                    {/* Bolinha */}
                    <div style={{
                        position: "absolute", top: "50%", left: pct + "%",
                        transform: "translate(-50%, -50%)",
                        width: 12, height: 12, borderRadius: "50%",
                        background: cor, boxShadow: `0 0 6px ${cor}88`,
                        opacity: src ? 1 : 0,
                        transition: arrastando ? "none" : "left 0.1s",
                    }} />
                </div>
                {/* Tempos */}
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 10, color: T.textoSecundario }}>{fmt(progresso)}</span>
                    <span style={{ fontSize: 10, color: T.textoSecundario }}>{fmt(duracao)}</span>
                </div>
            </div>

            {/* Controles principais */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, marginBottom: 14 }}>
                {/* Voltar 10s */}
                <button
                    onClick={() => pular(-10)}
                    disabled={!src}
                    style={{
                        background: "none", border: "none", cursor: src ? "pointer" : "default",
                        color: src ? T.texto : T.borda,
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                        opacity: src ? 1 : 0.3,
                    }}
                >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" fill="currentColor"/>
                        <text x="12" y="15" textAnchor="middle" fontSize="6" fill="currentColor" fontWeight="bold">10</text>
                    </svg>
                </button>

                {/* Play/Pause — botão principal */}
                <button
                    onClick={togglePlay}
                    style={{
                        width: 56, height: 56, borderRadius: "50%",
                        background: src ? `linear-gradient(135deg, ${cor}, ${cor}cc)` : T.borda,
                        border: "none", cursor: src ? "pointer" : "default",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: src && tocando ? `0 6px 20px ${cor}55` : "none",
                        transition: "all 0.2s",
                        transform: tocando ? "scale(0.95)" : "scale(1)",
                    }}
                >
                    {tocando ? (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                            <rect x="6" y="4" width="4" height="16" rx="1"/>
                            <rect x="14" y="4" width="4" height="16" rx="1"/>
                        </svg>
                    ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                            <path d="M8 5v14l11-7z"/>
                        </svg>
                    )}
                </button>

                {/* Avançar 10s */}
                <button
                    onClick={() => pular(10)}
                    disabled={!src}
                    style={{
                        background: "none", border: "none", cursor: src ? "pointer" : "default",
                        color: src ? T.texto : T.borda,
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                        opacity: src ? 1 : 0.3,
                    }}
                >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z" fill="currentColor"/>
                        <text x="12" y="15" textAnchor="middle" fontSize="6" fill="currentColor" fontWeight="bold">10</text>
                    </svg>
                </button>
            </div>



            {!src && (
                <div style={{ textAlign: "center", fontSize: 12, color: T.textoSecundario, marginTop: 10, opacity: 0.6 }}>
                    Nenhum áudio carregado
                </div>
            )}
        </div>
    );
}